import { addNotify } from 'features/notify';
import { capitalizeFirstLetter } from 'modules/Casino/utils/capitalizeFirstLetter';

const actionTypes = {
  GET_GAME_LIST_SUCCESS: 'slots/GET_GAME_LIST_SUCCESS',
  GET_GAME_LIST_REPLACED: 'slots/GET_GAME_LIST_REPLACED',
  GET_EXTRA_DATA_SUCCESS: 'slots/GET_EXTRA_DATA_SUCCESS',
  CLEAR_EXTRA_DATA: 'slots/CLEAR_EXTRA_DATA',
  ACTION_PROCESSING: 'slots/ACTION_PROCESSING',
  ACTION_PROCESSING_BY_PROVIDER: 'slots/ACTION_PROCESSING_BY_PROVIDER',
  ACTION_PROCESSING_SUCCESS: 'slots/ACTION_PROCESSING_SUCCESS',
  ACTION_FAILURE: 'slots/ACTION_FAILURE',
  LOAD_SESSION: 'slots/LOAD_SESSION',
  CLEAR_SESSION: 'slots/CLEAR_SESSION',
  GET_GAME_LIST_SUCCESS_IN_FIELD: 'slots/GET_GAME_LIST_SUCCESS_IN_FIELD',
  SET_CONTAINER_PROPS: 'slots/SET_CONTAINER_PROPS',
  INJECT_PROVIDER: 'slots/INJECT_PROVIDER',
};

function injectProvider({ provider, providerFrom }) {
  return async (dispatch, getState) => {
    dispatch({
      type: actionTypes.INJECT_PROVIDER,
      payload: {
        provider,
        providerFrom,
      },
    });
  };
}

function getGameList({ provider, platform = '' }) {
  return async (dispatch, getState, extra) => {
    const configuration = await import('modules/Casino/configuration');
    const slotProvidersConfiguration = configuration.default;

    const { api } = extra;
    const state = getState();
    const { isAuth, role } = state.auth;

    const data = slotProvidersConfiguration[provider];

    if (Array.isArray(provider)) {
      provider.forEach(t => {
        dispatch(getGameList({ provider: t }));
      });
      return;
    }
    if (!data) {
      throw new SyntaxError(`Provider "${provider}" not found in configuration`);
    }
    const providerList = state.casino.gameList.filter(t => t.routeProvider === provider);
    const favoriteSlots = state.casino.favorite;
    const listHasDefaultList = providerList.some(t => t.isDefault);

    if (provider === 'favorite' && favoriteSlots.length) {
      return;
    }

    if (data.defaultList && !isAuth) {
      if (!listHasDefaultList) {
        let { defaultList } = data;
        const defaultListModule = await import(
          // eslint-disable-next-line prefer-template
          'modules/Casino/providersData/' + capitalizeFirstLetter(provider) + '/defaultList'
        );

        defaultList = defaultListModule && defaultListModule.default ? defaultListModule.default : [];

        dispatch({
          type: actionTypes.GET_GAME_LIST_REPLACED,
          payload: { gameList: defaultList, provider },
        });
      }
    }

    if (data.extraData) {
      dispatch({
        type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
        payload: { value: true, provider },
      });
    }

    let extraData = state.casino.extraData?.[provider] || {};

    const isExtraDataExist = provider in state.casino.extraData;

    if (!isExtraDataExist) {
      const extraDataResponses = await Promise.all(
        data.extraData?.map(async _extraData => {
          if (_extraData.authRequired && !isAuth) return { data: {} };
          const { url, converter } = _extraData;
          const result = await api.casino.getExtraData({ url, converter, role, isAuth });
          if (result.success) {
            dispatch({
              type: actionTypes.GET_EXTRA_DATA_SUCCESS,
              payload: { provider, data: result.data },
            });
          }
          return result;
        }) || [],
      );

      extraDataResponses.forEach(extraDataParam => {
        extraData = {
          ...extraData,
          ...extraDataParam.data,
        };
      });
    }

    if (
      providerList.length &&
      !listHasDefaultList &&
      (!data.extraData || (data.extraData.length && Object.keys(state.casino.extraData[provider] || {}).length))
    ) {
      dispatch({
        type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
        payload: { value: false, provider },
      });
      return;
    }

    if (!isAuth && providerList.length) {
      dispatch({
        type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
        payload: { value: false, provider },
      });
      return;
    }

    if (!data.extraData) {
      dispatch({
        type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
        payload: { value: true, provider },
      });
    }

    const response = await api.casino.getGameList({
      provider,
      extraData,
      platform,
      role,
      isAuth,
    });

    if (response.success) {
      if (!data.uniqueField) {
        dispatch({
          type: actionTypes.GET_GAME_LIST_REPLACED,
          payload: { gameList: response.data, provider },
        });
      } else {
        dispatch({
          type: actionTypes.GET_GAME_LIST_SUCCESS_IN_FIELD,
          payload: { gameList: response.data, provider },
        });
      }
    } else {
      dispatch({
        type: actionTypes.ACTION_FAILURE,
        payload: `${response.errorMessage} ${provider}`,
      });
    }
    dispatch({
      type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
      payload: { value: false, provider },
    });
  };
}

function getSessionUrl({ provider, gameId, game, withRedirect = false, isDesktop = false }) {
  return async (dispatch, getState, extra) => {
    dispatch({
      type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
      payload: { value: true, provider },
    });
    const { api } = extra;
    const state = getState();
    const { isAuth, role } = state.auth;
    const { lang } = state.locale;
    const extraData = state.casino.extraData[provider] || {};
    const { gameList } = state.casino;
    const userId = state.auth.user?.id ?? -1;
    const { origin } = window.location;
    const providerGameList = gameList.filter(t => t.routeProvider === provider);
    const response = await api.casino.getSessionUrl({
      provider,
      gameId,
      game,
      extraData,
      gameList: providerGameList,
      params: { lang, isDesktop, userId, url: origin, game },
      role,
      isAuth,
    });
    if (response.success) {
      dispatch({ type: actionTypes.LOAD_SESSION, payload: response.data });
      if (withRedirect) location.href = response.data;
    } else {
      dispatch({
        type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
        payload: { value: false, provider },
      });
      dispatch({
        type: actionTypes.ACTION_FAILURE,
        payload: response.errorMessage,
      });
    }
    if (!withRedirect)
      dispatch({
        type: actionTypes.ACTION_PROCESSING_BY_PROVIDER,
        payload: { value: false, provider },
      });
  };
}

function clearSessionUrl() {
  return async dispatch => {
    dispatch({ type: actionTypes.CLEAR_SESSION });
  };
}

const setContainerProps =
  ({ onScroll, containerRef }) =>
  async dispatch => {
    dispatch({
      type: actionTypes.SET_CONTAINER_PROPS,
      payload: { onScroll, containerRef },
    });
  };

function clearExtraData() {
  return async dispatch => {
    dispatch({
      type: actionTypes.CLEAR_EXTRA_DATA,
    });
  };
}

export { actionTypes, getGameList, getSessionUrl, clearSessionUrl, setContainerProps, clearExtraData, injectProvider };
